<template>
  <div class="ai-logic-wrapper">
    <div class="modal task">
      <div class="modal-close" @click="close"></div>
      <div class="custom-modal-header">AI logic</div>
      <div class="content">
        <!-- <div class="run-code-button" @click="runUserCode">
          <i v-if="!isLoading" class="fas fa-play"></i>
          <span class="p-4" v-if="isLoading">
            <b-spinner class="call-spinner" label="Spinning"></b-spinner>
          </span>
        </div> -->
        <ul class="left-side-options top-left">
          <li class="run-code" v-b-tooltip.hover title="Ausführen">
            <a v-if="!isLoading" class="run-code" @click="runUserCode">
              <i class="fas fa-play"></i>
              <span class="accessibility">Run</span>
            </a>
            <b-spinner
              v-if="isLoading"
              class="call-spinner"
              label="Spinning"
            ></b-spinner>
          </li>
        </ul>
        <div class="logic-editor">
          <div class="input-wrapper">
            <AILogicInputOutput :results="task.input" />
          </div>
          <div class="editor">
            <AILogicEditor
              ref="editor"
              :editorNodes.sync="task.props.nodes"
              :endPoint="flowRunnerEndpoint"
              @onTextSelect="onTextSelection"
              @onMouseDown="toggleFloatingDropdown"
              @onCodeGenerate="onTurnIntoCode"
              :action="currentAiActions"
            />
            <div class="floating-dropdown hide" ref="floatingDropdown">
              <div v-if="isDropdownVisible" class="dropdown-overlay"></div>
              <AILogicDropdown
                v-if="isDropdownVisible"
                @close="onCloseAILogicDropdown"
                :actions="currentAiActions"
                @onTurnIntoCode="onTurnIntoCode"
                @toggleNodeView="toggleNodeView"
                @onUpdateReasoning="onUpdateReasoning"
              />
            </div>
          </div>
          <div class="output-wrapper">
            <AILogicInputOutput :results="task.output" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// prism editor
import { PrismEditor } from 'vue-prism-editor';
import { highlight, languages } from 'prismjs/components/prism-core';

// ui components
import AILogicDropdown from '@/components/AILogic/AILogicDropdown.vue';
import AILogicInputOutput from '@/components/AILogic/AILogicInputOutput.vue';
import AILogicEditor from '@/components/AILogic/AILogicEditor.vue';
// services
import { askChatGPT } from '../../services/workflow.service';
import { runCode } from '../../services/axios/flow-runner.api';

// enums
import { aiLogiceditorNodeViewType } from '@/utils/constants/AILogicConstants';

export default {
  data() {
    return {
      isLoading: false,
      isDropdownVisible: false,
      aiActions: [
        {
          name: 'turnIntoCode',
          icon: 'arrow-up-circle-fill',
          action: 'onTurnIntoCode',
          isLoading: false,
        },
        {
          name: 'togglecode',
          icon: 'code-slash',
          action: 'toggleNodeView',
          isLoading: false,
        },
        {
          name: 'updateReasoning',
          icon: 'stars',
          action: 'onUpdateReasoning',
          isLoading: false,
        },
      ],
      currentAiActions: [],
      jsonTypes: ['object', 'objectlist', 'list'],
      modelTypes: [
        'timeseries', // timeseries is needed for backwards compatiblility
        'loadprofile',
        'custom',
      ],
      listTypes: ['checkbox'],
    };
  },
  props: {
    task: {
      type: Object,
      default: () => {},
    },
    process: {
      type: Object,
    },
    flowRunnerEndpoint: {
      type: String,
    },
    isTableIdArray: { type: Boolean, default: false },
  },

  methods: {
    close() {
      this.$set(this.task, 'output', this.task.output.concat(this.task.input));
      this.$emit('close');
    },
    highlighter(code) {
      return highlight(code, languages.py);
    },
    onCloseAILogicDropdown(event) {
      const editor = event.target.closest('.ai-logic-editor');
      if (!editor) {
        this.toggleFloatingDropdown(null);
      }
    },
    setSelectionRange() {
      if (this.selectedRange) {
        const selection = window.getSelection();
        selection.removeAllRanges(); // Clear existing selection
        selection.addRange(this.selectedRange); // Restore saved selection
      }
    },
    getPrevTaskInputsForPropmts() {
      const inputAsDict = this.task.input?.reduce((acc, item) => {
        if (!Array.isArray(this.task.input) || this.task.input?.length === 0) {
          return {};
        }
        acc[item.name] = item.value;
        return acc;
      }, {});

      return JSON.stringify(inputAsDict);
    },
    async onTurnIntoCode(item) {
      try {
        this.setSelectionRange();
        item.isLoading = true;
        const nodes = this.task.props.nodes;
        const selectedNodes = this.$refs?.editor?.getSelectedBlockIds();
        const indexes = selectedNodes
          .map((id) => nodes.findIndex((n) => n.id == id))
          .sort((a, b) => a - b);
        const mergedText = indexes.map((i) => nodes[i].SNL).join('\n');
        const promptMessage = `Assume you are ai python code generator. You should follow the defined steps for processing data. 
        1. you are provided with the user natural language prompt called UNL.
        2. You have to generate python code for the provided UNL. 
        3. Do not give any suggestions, texts or print statments, you should only return code.
        4. Wherever this code will be used, you have to assume that a results dictionary is already defined there, So any final results of the provided code are to be stored inside the results dictionary.
        5. Do not declare or initialise the 'results = {}' in the provided code but assume that its already declared.
          UNL = ${mergedText}
          input =  ${this.getPrevTaskInputsForPropmts()}
          existing code = ${this.$refs?.editor?.getCode()}
         `;
        const formData = new FormData();
        formData.append(
          'props',
          JSON.stringify({ prompt: promptMessage, model: 'gpt-4' })
        );
        formData.append('usecase', 'ailogic_code');
        const res = await askChatGPT(this.flowRunnerEndpoint, formData);
        const sanitizedCode = res.data.message.replace(
          /^```python\s*|\s*```$/g,
          ''
        );

        this.setSelectionRange();
        const selectedNodeElement = this.$refs?.editor?.getClosestNodeBlock();
        const selectedNode = this.task.props.nodes.find(
          (node) => node.id == selectedNodeElement.id
        );

        if (selectedNode) {
          selectedNode.code = sanitizedCode;
        }
        this.$refs?.editor?.mergeSelectedNodes();
        this.$refs?.editor?.clearSelection();
        this.toggleFloatingDropdown(null);
        setTimeout(() => {
          this.$refs?.editor?.updateNodeText(selectedNode);
        });
      } catch (error) {
        this.$emit(
          'showToaster',
          'Fehler beim Generieren des Codes.',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        item.isLoading = false;
      }
    },
    toggleNodeView() {
      this.setSelectionRange();
      const selectedNodes = this.$refs?.editor?.getSelectedBlockIds();

      const firstNode = this.task.props.nodes.find(
        (node) => node.id == selectedNodes[0]
      );
      const updatedView = firstNode.view === 'NL' ? 'CODE' : 'NL';
      this.task.props.nodes.forEach((node) => {
        if (selectedNodes.includes(node.id) && node.code) {
          node.view = updatedView;
          setTimeout(() => {
            this.$refs.editor?.updateNodeText(node);
          });
        }
      });
    },

    async onUpdateReasoning(item) {
      try {
        item.isLoading = true;
        const selection = window.getSelection();
        this.setSelectionRange();
        const selectedNodesId = this.$refs?.editor?.getSelectedBlockIds();
        const selectedNodes = this.task.props.nodes.filter((node) =>
          selectedNodesId.includes(node.id)
        );
        const promptMessage = `Your task is to take code from updated_code and understand code and generate Standard natural languages based of code. Follow these steps:
          1. Understand code from updated_code.
          2. convert code into Standard Natural Language in user readable formate and use understable.
          3. Ensure the sentence follows proper syntax and formal writing conventions.
          4. Don't give code only convert given code to Standard Natural Language.
          5. Standard Natural Language is user understable and small line not fully describe it's in brife.
          6. Give responce in standard language not in json formate it's not user readable formate.
          7. Don't Give any additional data only give Natural language.
          7. Give only Standard Natural Language. Not any other text.
          8. strickly follow above all instructions.
          updated_code: ${selectedNodes[0].code};
          selected part: ${selection.toString()};
          existing data : ${JSON.stringify(selectedNodes)};
        `;
        const formData = new FormData();
        formData.append(
          'props',
          JSON.stringify({ prompt: promptMessage, model: 'gpt-4' })
        );
        formData.append('usecase', 'ailogic_reasoning');

        const res = await askChatGPT(this.flowRunnerEndpoint, formData);
        // const sanitizedCode = res.data.message;
        // selectedNodes[0].SNL = sanitizedCode;
        // return;
        const jsonMatch = res.data.message.match(
          /```json\s*\n*(\[.*\])\s*\n*```/s
        );
        if (jsonMatch) {
          try {
            const jsonString = jsonMatch[1]; // Extracted JSON
            const updatedData = JSON.parse(jsonString);
            const updatedDataMap = new Map(
              updatedData.map((node) => [node.id, node])
            );
            selectedNodes.forEach((selectedNode) => {
              const updatedNode = updatedDataMap.get(selectedNode.id);
              if (updatedNode) {
                Object.assign(selectedNode, updatedNode);
              }
            });
          } catch (error) {
            throw error;
          }
        }
        this.$refs?.editor?.clearSelection();
        this.toggleFloatingDropdown(null);
        selectedNodes.forEach((node) => {
          this.$refs?.editor?.updateNodeText(node);
        });
      } catch (error) {
        this.$emit(
          'showToaster',
          'Fehler beim Aktualisieren der Änderungen.',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        item.isLoading = false;
        this.toggleFloatingDropdown(null);
      }
    },

    async runUserCode() {
      try {
        this.isLoading = true;
        const formData = new FormData();
        const action_data = JSON.parse(JSON.stringify(this.process));

        if (
          action_data.formTrigger != null &&
          action_data.formTrigger.length > 0
        ) {
          action_data.formTrigger.forEach((data) => {
            data.value = data.value.id;
          });
          action_data.tasks.forEach((t) => {
            t.input = [];
            t.output = [];
          });
        }
        let f = JSON.parse(JSON.stringify(action_data));
        formData.append('flow', JSON.stringify(f));
        formData.append('props', JSON.stringify(this.task.props));
        formData.append('task-id', this.task.id);

        // Put input in form data
        if (this.task.input) {
          this.task.input.forEach((e) => {
            if (e.type == 'file') formData.append(e.name, e.value);
            else if (this.jsonTypes.includes(e.type))
              formData.append(e.name, JSON.stringify(e.value));
            else if (this.modelTypes.includes(e.type))
              formData.append(e.name, e.value.id);
            else if (this.listTypes.includes(e.type)) {
              e.value.forEach((el) => formData.append(e.name, el));
            } else if (e.type === 'dataframe') {
              formData.append(e.name, JSON.stringify(e.value));
            } else formData.append(e.name, e.value);
          });
        }

        const res = await runCode(this.flowRunnerEndpoint, formData);
        this.task.output = res.data;
      } catch (error) {
        this.showToaster(
          'Bitte beheben Sie den Fehler im Code !',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        this.isLoading = false;
      }
    },
    onTextSelection() {
      const selection = window.getSelection();
      const selectedText = selection.toString();
      if (!selectedText.length) return;
      this.selectedRange = selection.getRangeAt(0);
      const focusNode = selection.focusNode;
      const focusOffset = selection.focusOffset;

      const focusRange = document.createRange();
      focusRange.setStart(focusNode, focusOffset);
      focusRange.setEnd(focusNode, focusOffset);
      const rect = focusRange.getBoundingClientRect();
      if (!rect.x && !rect.y) return;
      this.toggleFloatingDropdown(rect);
    },
    toggleFloatingDropdown(rect = null) {
      const floatingDropdown = this.$refs.floatingDropdown;
      if (!floatingDropdown) return;

      if (!rect) {
        this.isDropdownVisible = false;
        floatingDropdown.classList?.add?.('hide');
        this.selectedRange = null;
      } else {
        const selectedElement = this.$refs?.editor?.getClosestNodeBlock();
        this.isDropdownVisible = true;
        const selectedNode = this.task.props.nodes.find(
          (node) => node.id == selectedElement.id
        );
        if (!selectedNode.code) {
          this.currentAiActions = this.aiActions.filter(
            (action) => action.name == 'turnIntoCode'
          );
        } else {
          this.currentAiActions = this.aiActions.filter(
            (action) => action.name != 'turnIntoCode'
          );
        }
        if (rect.top) {
          floatingDropdown.style.top = rect.top + 'px';
          floatingDropdown.style.left = rect.right + 20 + 'px';
          floatingDropdown.classList?.remove?.('hide');
        }
      }
    },
  },

  created() {
    this.task.output = [];
  },

  components: {
    PrismEditor,
    AILogicDropdown,
    AILogicInputOutput,
    AILogicEditor,
  },
};
</script>

<style scoped>
.ai-logic-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-logic-wrapper .content {
  height: 100%;
}
/* editor styles */

.ai-logic-wrapper .logic-editor {
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid #eee;
  height: 100%;
  display: grid;
  grid-template-rows: 1fr 3fr 1fr;
}
.ai-logic-wrapper .input-wrapper {
  border-bottom: 2px solid #eee;
  height: 150px;
}
.ai-logic-wrapper .output-wrapper {
  border-top: 2px solid #eee;
  height: 150px;
}
.ai-logic-wrapper .editor {
  box-shadow: none !important;
  border: none;
  height: 100%;
  min-height: 0;
}

.ai-logic-wrapper .floating-dropdown {
  position: fixed;
  z-index: 10;
}
.ai-logic-wrapper .floating-dropdown.hide {
  display: none;
}
.ai-logic-wrapper .dropdown-overlay {
  position: fixed;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
}
.ai-logic-wrapper .run-code-button {
  position: absolute;
  /* left: -30px; */
  top: 32px;
  left: 36px;
  border-radius: 50%;
  border: 1px solid black;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: black;
}

/* run button */
.ai-logic-wrapper .top-left {
  position: absolute;
  top: 32px;
  padding: 0;
  left: 36px;
  cursor: pointer;
}
.ai-logic-wrapper .left-side-options > li {
  display: inline-block;
}
.ai-logic-wrapper .left-side-options > li > a,
.ai-logic-wrapper .left-side-options > span > a {
  border: 1px solid #dbdde0;
  border-radius: 50%;
  height: 42px;
  width: 42px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  color: #000;
  background: #fff;
  -webkit-transition: color 0.15s;
  transition: color 0.15s;
  -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.13);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.13);
  text-decoration: none;
}

.ai-logic-wrapper .accessibility {
  z-index: 30;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 0px !important;
}
</style>
